import React, { useState, useEffect, useRef } from 'react';
import PictureImg from '../PictureImg';

const AboutSection = () => {
  const [aboutAnimated, setAboutAnimated] = useState(false);
  const aboutRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return undefined;

    const handlePlaying = () => {
      videoEl.classList.add('about-video-visible');
    };
    videoEl.addEventListener('playing', handlePlaying);

    const tryPlayVideo = () => {
      const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const saveData = navigator.connection && navigator.connection.saveData;
      if (reducedMotion || saveData) return;
      if (navigator.webdriver || /HeadlessChrome|Prerender|bot|crawler|spider/i.test(navigator.userAgent)) return;

      const src = window.innerWidth <= 640 ? '/api/img/about-tuscany-640.mp4' : '/api/img/about-tuscany.mp4';
      videoEl.src = src;
      videoEl.load();
      const playPromise = videoEl.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    };

    if (document.readyState === 'complete') {
      tryPlayVideo();
    } else {
      window.addEventListener('load', tryPlayVideo, { once: true });
    }

    return () => {
      videoEl.removeEventListener('playing', handlePlaying);
      window.removeEventListener('load', tryPlayVideo);
    };
  }, []);

  useEffect(() => {
    const aboutObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !aboutAnimated) {
          setAboutAnimated(true);
        }
      },
      { threshold: 0.2 }
    );

    if (aboutRef.current) {
      aboutObserver.observe(aboutRef.current);
    }

    return () => {
      if (aboutRef.current) {
        aboutObserver.unobserve(aboutRef.current);
      }
    };
  }, [aboutAnimated]);

  return (
    <section 
      id="about" 
      className="py-20 px-6 bg-white"
      ref={aboutRef}
    >
      <div className="container mx-auto max-w-7xl">
        <div className={`grid md:grid-cols-2 gap-12 items-center ${aboutAnimated ? 'about-animate-active' : 'about-animate-ready'}`}>
          <div className="space-y-6 order-2 md:order-1">
            <div className="about-text-1">
              <span className="text-blue-900 font-semibold tracking-wide uppercase text-sm">
                About ALL-FOOD
              </span>
              <h2 className="md:text-5xl !font-bold !text-4xl mt-3 text-stone-900">
                Italian food supplier based in Tuscany
              </h2>
            </div>
            <p className="!text-lg text-stone-600 about-text-2">
              Based in the heart of Tuscany, ALL-FOOD acts as your dedicated <span className="text-blue-900 font-semibold">Italian Export Office</span>. We bridge the gap between Italy&apos;s exceptional food producers and international markets, providing end-to-end support, from product sourcing to final delivery.
            </p>
            <p className="text-lg text-stone-600 leading-relaxed about-text-3">
              <span className="font-bold text-stone-800">Our mission</span> is to make authentic Italian quality accessible to importers and retailers worldwide. With deep roots in Italian food culture, we ensure every product meets the highest standards of authenticity while managing the logistics for a seamless experience.
            </p>
          </div>
          <div className={`relative order-1 md:order-2 about-image-wrapper ${aboutAnimated ? 'about-image-active' : ''}`}>
            <div className="about-image-container relative w-full h-[400px] md:h-[500px] flex items-center justify-center bg-stone-50 rounded-2xl">
              <PictureImg
                base="/api/img/about-tuscany"
                alt="Tuscany Landscape - ALL-FOOD"
                className="about-image max-h-full object-contain"
                width={1366}
                height={768}
                lcp={true}
              />
              <video
                ref={videoRef}
                className="about-video"
                width={1366}
                height={768}
                poster="/api/img/about-tuscany.webp"
                muted
                loop
                playsInline
                preload="none"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
